// Advanced Search Hook - จัดการ state และ logic สำหรับระบบค้นหาขั้นสูง
import { useState, useCallback, useEffect } from 'react';
import { SearchFilter, SearchField } from '../components/search/AdvancedSearchModal';

export interface UseAdvancedSearchOptions {
  searchFields: SearchField[];
  onSearch?: (filters: Record<string, any>) => void;
  defaultFilters?: Record<string, any>;
  storageKey?: string; // สำหรับบันทึก saved filters ใน localStorage
}

export interface UseAdvancedSearchReturn {
  // Search state
  currentFilters: Record<string, any>;
  isSearchModalOpen: boolean;
  
  // Saved filters
  savedFilters: SearchFilter[];
  
  // Actions
  openSearchModal: () => void;
  closeSearchModal: () => void;
  handleSearch: (filters: Record<string, any>) => void;
  handleSaveFilter: (filter: Omit<SearchFilter, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  handleDeleteFilter: (filterId: string) => Promise<void>;
  handleClearFilters: () => void;
  
  // Utils
  hasActiveFilters: boolean;
  getActiveFilterCount: () => number;
  getFilterSummary: () => string[];
}

const useAdvancedSearch = (options: UseAdvancedSearchOptions): UseAdvancedSearchReturn => {
  const {
    searchFields,
    onSearch,
    defaultFilters = {},
    storageKey = 'advanced_search_filters'
  } = options;

  // Search state
  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>(defaultFilters);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  
  // Saved filters state
  const [savedFilters, setSavedFilters] = useState<SearchFilter[]>([]);

  // Load saved filters from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsedFilters = JSON.parse(saved);
        setSavedFilters(parsedFilters);
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  }, [storageKey]);

  // Save filters to localStorage whenever savedFilters changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(savedFilters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [savedFilters, storageKey]);

  // Modal actions
  const openSearchModal = useCallback(() => {
    setIsSearchModalOpen(true);
  }, []);

  const closeSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
  }, []);

  // Search actions
  const handleSearch = useCallback((filters: Record<string, any>) => {
    setCurrentFilters(filters);
    
    if (onSearch) {
      onSearch(filters);
    }
  }, [onSearch]);

  const handleClearFilters = useCallback(() => {
    setCurrentFilters({});
    
    if (onSearch) {
      onSearch({});
    }
  }, [onSearch]);

  // Saved filters actions
  const handleSaveFilter = useCallback(async (filter: Omit<SearchFilter, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newFilter: SearchFilter = {
      ...filter,
      id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setSavedFilters(prev => [...prev, newFilter]);
  }, []);

  const handleDeleteFilter = useCallback(async (filterId: string) => {
    setSavedFilters(prev => prev.filter(filter => filter.id !== filterId));
  }, []);

  // Utils
  const hasActiveFilters = Object.keys(currentFilters).some(key => {
    const value = currentFilters[key];
    return value !== undefined && value !== '' && value !== null && 
           !(typeof value === 'object' && Object.keys(value).length === 0);
  });

  const getActiveFilterCount = useCallback(() => {
    return Object.keys(currentFilters).filter(key => {
      const value = currentFilters[key];
      return value !== undefined && value !== '' && value !== null &&
             !(typeof value === 'object' && Object.keys(value).length === 0);
    }).length;
  }, [currentFilters]);

  const getFilterSummary = useCallback(() => {
    const summary: string[] = [];
    
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        const field = searchFields.find(f => f.key === key);
        const fieldLabel = field?.label || key;
        
        if (typeof value === 'object' && value !== null) {
          // Handle date range
          if (value.from || value.to) {
            const from = value.from ? new Date(value.from).toLocaleDateString('th-TH') : '';
            const to = value.to ? new Date(value.to).toLocaleDateString('th-TH') : '';
            if (from && to) {
              summary.push(`${fieldLabel}: ${from} - ${to}`);
            } else if (from) {
              summary.push(`${fieldLabel}: จาก ${from}`);
            } else if (to) {
              summary.push(`${fieldLabel}: ถึง ${to}`);
            }
          }
        } else {
          // Handle simple values
          let displayValue = value.toString();
          
          // For select fields, try to get the label
          if (field?.type === 'select' && field.options) {
            const option = field.options.find(opt => opt.value === value);
            displayValue = option?.label || displayValue;
          }
          
          // For boolean fields
          if (field?.type === 'boolean') {
            displayValue = value === true ? 'ใช่' : 'ไม่ใช่';
          }
          
          summary.push(`${fieldLabel}: ${displayValue}`);
        }
      }
    });
    
    return summary;
  }, [currentFilters, searchFields]);

  return {
    // Search state
    currentFilters,
    isSearchModalOpen,
    
    // Saved filters
    savedFilters,
    
    // Actions
    openSearchModal,
    closeSearchModal,
    handleSearch,
    handleSaveFilter,
    handleDeleteFilter,
    handleClearFilters,
    
    // Utils
    hasActiveFilters,
    getActiveFilterCount,
    getFilterSummary
  };
};

export default useAdvancedSearch;